<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Sidebar from '@pages/Shared/sidebar/AdminSidebar.vue'
import Loader from '@pages/Shared/AppLoader.vue'
import Nav from '@pages/Shared/navs/Nav.vue'
import SidebarItem from '@pages/Shared/SidebarItem.vue'
import { defineComponent, h } from 'vue'
import {
  PhHouse, PhDotsThreeCircle, PhUser, PhUserCircle,
  PhStudent, PhBuilding, PhBank, PhBook, PhNotePencil,
  PhArticle, PhPersonSimpleRun, PhCalendar, PhPencilSimple,
  PhScroll, PhClipboardText, PhMicrophone, PhGraduationCap,
  PhUserList,
} from '@phosphor-icons/vue'

const openSidebar   = ref(false)
const toggleSidebar = () => { openSidebar.value = !openSidebar.value }
const closeSidebar  = () => { openSidebar.value = false }
const sidebarRef = ref(null)

const page             = usePage()
const can              = computed(() => page.props?.can || {})
const currentComponent = computed(() => page?.component || '')
const isActive = (...pages) => pages.flat().includes(currentComponent.value)

watch(currentComponent, async () => {
  await nextTick()
  sidebarRef.value?.scrollActiveIntoView()
}, { immediate: true })

const NairaIcon = defineComponent({
  props: { size: { type: Number, default: 18 } },
  render() {
    return h('svg', {
      xmlns: 'http://www.w3.org/2000/svg',
      width: this.size, height: this.size,
      viewBox: '0 0 24 24', fill: 'none',
      stroke: 'currentColor', 'stroke-width': '1.5',
      'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    }, [
      h('path', { d: 'M7 19V5l10 14V5' }),
      h('path', { d: 'M5 9.5h14' }),
      h('path', { d: 'M5 13.5h14' }),
    ])
  },
})

const sections = computed(() => [
  {
    title: 'General',
    items: [
      { title: 'Dashboard', url: '/admin/dashboard', icon: PhHouse, pages: ['Dashboard/Index'] },
    ],
  },
  {
    title: 'Admin',
    items: [
      { title: 'Roles', url: '/admin/roles', icon: PhDotsThreeCircle, pages: ['admin/Roles/Index'], permission: can.value.viewAnyRole },
      { title: 'Users', url: '/admin/users', icon: PhUser,            pages: ['admin/Users/Index'], permission: can.value.viewAnyUser },
    ],
  },
  {
    title: 'Management',
    items: [
      { title: 'Sessions/Terms', url: '/admin/academic-sessions', icon: PhCalendar,    pages: ['admin/AcademicSessions/Index'] },
      { title: 'Staff',          url: '/admin/staff',             icon: PhUserCircle,  pages: ['admin/Staff/Index'],    permission: can.value.viewAnyStaff },
      { title: 'Students',       url: '/admin/students',          icon: PhStudent,     pages: ['admin/Students/Index'], permission: can.value.viewAnyStudent },
      { title: 'Sections',       url: '/admin/sections',          icon: PhBuilding,    pages: ['admin/Sections/Index'], permission: can.value.viewAnySection },
      { title: 'Classes',        url: '/admin/classes',           icon: PhBank,        pages: ['admin/Classes/Index'],  permission: can.value.viewAnyClass },
      { title: 'Subjects',       url: '/admin/subjects',          icon: PhBook,        pages: ['admin/Subjects/Index'], permission: can.value.viewAnySubject },
    ],
  },
  {
    title: 'Admission',
    items: [
      { title: 'Admissions',   url: '/admin/admissions',   icon: PhGraduationCap, pages: ['admin/Admissions/Index', 'admin/Admissions/Show', 'admin/Admissions/Print'] },
      { title: 'Applicants',   url: '/admin/applicants',   icon: PhUserList,      pages: ['admin/Applicants/Index', 'admin/Applicants/Show'] },
      { title: 'Applications', url: '/admin/applications', icon: PhClipboardText, pages: ['admin/Applications/Index', 'admin/Applications/Show'] },
      { title: 'Interviews',   url: '/admin/interviews',   icon: PhMicrophone,    pages: ['admin/Interviews/Index'] },
      { title: 'Payments',     url: '/admin/payments',     icon: NairaIcon,       pages: ['admin/Payments/Index', 'admin/Payments/Show'] },
    ],
  },
  {
    title: 'Examination',
    items: [
      { title: 'Score-sheets',url: '/admin/score-sheets',icon: PhArticle,         pages: ['admin/Scoresheets/Index'], permission: can.value.viewAnyScoreSheet },
      { title: 'Assessments', url: '/admin/assessments', icon: PhNotePencil,      pages: ['admin/Assessments/Index'] },
      { title: 'Scores',      url: '/admin/scores',      icon: PhPencilSimple,    pages: ['admin/Scores/Index'] },
      { title: 'Results',     url: '/admin/results',     icon: PhScroll,          pages: ['admin/Results/Index'] },
      { title: 'Promotions',  url: '/admin/promotions',  icon: PhPersonSimpleRun, pages: ['admin/Promotions/Index'] },
    ],
  },
])
</script>

<template>
  <div class="bg-background min-h-screen">

    <Sidebar ref="sidebarRef" :openSidebar="openSidebar" @close="closeSidebar">
      <template v-for="section in sections" :key="section.title">

        <p class="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-white/30"
          :class="section.title === 'General' ? 'pt-1' : 'pt-4'">
          {{ section.title }}
        </p>

        <template v-for="item in section.items" :key="item.title">
          <SidebarItem
            v-if="item.permission === undefined || item.permission"
            :title="item.title"
            :url="item.url"
            :active="isActive(item.pages)"
          >
            <component :is="item.icon" :size="20" />
          </SidebarItem>
        </template>

      </template>
    </Sidebar>

    <Nav :sidebarOpen="openSidebar" @toggleSidebar="toggleSidebar" />

    <main class="pt-20 pb-10 px-6 md:ml-[250px] transition-all relative">
      <slot />
    </main>

    <Loader />
  </div>
</template>